import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

const channels = {
  questProgressByAddress: (userAddress: string) =>
    `QUEST_PROGRESS_${userAddress}` as const,
  poolTransactionsByAddress: (poolAddress: string) =>
    `POOL_TRANSACTIONS_${poolAddress}` as const,
  poolMarketCapByAddress: (poolAddress: string) =>
    `POOL_MARKET_CAP_${poolAddress}` as const,
  poolTransactions: () => `POOL_TRANSACTIONS` as const,
  poolCreated: () => `POOL_CREATED` as const,
  poolUpdated: (poolAddress: string) =>
    `POOL_UPDATED_${poolAddress}` as const,
  commentCreated: (poolAddress: string) =>
    `COMMENT_CREATED_${poolAddress}` as const,
  commentUpdated: (poolAddress: string) =>
    `COMMENT_UPDATED_${poolAddress}` as const,
  all: () => `ALL` as const,
}

type Callback = (data: any) => void
type Channels = Record<string, Record<string, Callback>>

type WebSocketProviderProps = {
  children: React.ReactNode
}

type WebSocketContextProps = {
  subscribe: (id: string, channel: string, callback: Callback) => void
  unsubscribe: (id: string, channel: string) => void
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(
  undefined,
)

const emptyChannels: Channels = {}

function WebSocketProvider({ children }: WebSocketProviderProps) {
  const url = process.env.REACT_APP_GOHORSE_URL

  const wsRef = useRef<WebSocket | undefined>(undefined)
  const channels = useRef<Channels>(emptyChannels) // maps each channel to the callback
  const [isConnected, setIsConnected] = useState(false)
  const pendingSubscriptions = useRef<{ channel: string; callback: any }[]>([])

  const subscribeInServer = useCallback((channel: string) => {
    wsRef.current?.send(JSON.stringify({ channel }))
  }, [])

  /* called from a component that registers a callback for a channel */
  const subscribe = useCallback(
    (id: string, channel: string, callback: any) => {
      channels.current[id] = {
        ...channels.current[id],
        [channel]: callback,
      }

      if (isConnected) {
        subscribeInServer(channel)
      } else {
        pendingSubscriptions.current.push({ channel, callback })
      }
    },
    [isConnected, subscribeInServer],
  )

  const unsubscribe = useCallback((id: string, channel: string) => {
    console.info('Unsubscribed from channel', id, channel)
    delete channels.current[id]
  }, [])

  useEffect(() => {
    try {
      wsRef.current = new WebSocket(url)
    } catch (error) {
      console.error('Error creating WebSocket', error)
      return
    }

    wsRef.current.onopen = () => {
      console.info(`Connected to WebSocket server at ${url}`)
      setIsConnected(true)

      pendingSubscriptions.current.forEach(({ channel }) => {
        subscribeInServer(channel)
      })
      pendingSubscriptions.current = []
    }

    wsRef.current.onclose = () => {
      console.info(`Disconnected from WebSocket server at ${url}`)
      setIsConnected(false)
    }

    wsRef.current.onerror = (error) => {
      console.error(`WebSocket error at ${url}`, error)
    }

    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data)
      const { channel } = message
      if (!!channel) {
        for (const id in channels.current) {
          if (channels.current[id][channel]) {
            channels.current[id][channel](message)
          }
        }
      }
    }

    return () => {
      wsRef.current.close()
    }
  }, [subscribeInServer, url])

  const values = useMemo(
    () => ({
      subscribe,
      unsubscribe,
    }),
    [subscribe, unsubscribe],
  )

  return (
    <WebSocketContext.Provider value={values}>
      {children}
    </WebSocketContext.Provider>
  )
}

const useWebSocketContext = () => useContext(WebSocketContext)

export { WebSocketContext, WebSocketProvider, useWebSocketContext, channels }
